// src/utils/slotHar.js
//
// Slot HAR (docs/slot-story-spec.md Contract E): a HAR holding exactly the
// requests ONE slot is made of - the rows its phases point at (Prebid, APS, GAM,
// impression, click), the mediation bill rows and the OMID beacon rows.
//
// Built on utils/buildHar.js (HAR 1.2) with the same `log._inspectra.<x>`
// convention evidenceHar.js uses. Pure and offline: no fetch, no storage.
//
// Both capture paths:
//  - MITM rows keep their response when the browser still holds the body; rows
//    whose body was trimmed (`_bodyStripped`) are listed in `slot.bodyStripped`
//    so a short entry says it is short.
//  - logcat rows are request-only: `response.status: 0`, empty content and
//    `comment: 'request-only'` (valid HAR 1.2 for "no response").

import { buildHarFromRequests } from './buildHar';
import { buildSlotStory } from './slotStory';

const lookupRow = (rowsByKey, k) => {
  if (k == null || !rowsByKey) return null;
  return (typeof rowsByKey.get === 'function' ? rowsByKey.get(k) : rowsByKey[k]) || null;
};

/**
 * Every rowKey the slot is made of, unique, in pipeline order:
 * phases.*.rowKey / rowKeys, mediation buyers' rowKeys, omidSessions[].rowKeys,
 * activeView[].rowKeys.
 */
export function slotRowKeys(lc) {
  const out = [];
  const seen = new Set();
  const add = (k) => { if (k != null && !seen.has(k)) { seen.add(k); out.push(k); } };
  const addPhase = (ph) => {
    if (!ph || typeof ph !== 'object') return;
    add(ph.rowKey);
    if (Array.isArray(ph.rowKeys)) ph.rowKeys.forEach(add);
  };
  const phases = (lc && lc.phases) || {};
  for (const id of ['prebid', 'amazon', 'gam', 'impression', 'click']) addPhase(phases[id]);
  const med = phases.mediation;
  if (med) {
    addPhase(med);
    for (const b of Array.isArray(med.buyers) ? med.buyers : []) addPhase(b);
  }
  for (const s of Array.isArray(lc && lc.omidSessions) ? lc.omidSessions : []) addPhase(s);
  // Google ActiveView pings attached to the slot (lifecycle.activeView[], one rowKeys entry per ping).
  for (const e of Array.isArray(lc && lc.activeView) ? lc.activeView : []) addPhase(e);
  return out;
}

/** True when the lifecycle's GAM row came from the USB/adb logcat tap. */
export function isLogcatLifecycle(lc) {
  const row = lc && lc.phases && lc.phases.gam && lc.phases.gam.row;
  return !!(row && row.captureSource === 'logcat');
}

const safe = (s) => String(s == null ? '' : s).replace(/[^A-Za-z0-9._-]/g, '_');

/** `slot-<adUnitTail>-<key>.har`, characters outside [A-Za-z0-9._-] replaced by `_`. */
export function slotHarFilename(lc) {
  const unit = String((lc && lc.adUnit) || '').split('/').filter(Boolean).pop() || 'slot';
  return `slot-${safe(unit)}-${safe(lc && lc.key)}.har`;
}

/**
 * buildSlotHar(lifecycle, rowsByKey) -> { har, missingIds, count, filename }
 * `rowsByKey` is a Map (or object) of `_k` -> captured row. Keys the browser no
 * longer holds are listed in `missingIds` and in `log._inspectra.slot`.
 */
export function buildSlotHar(lifecycle, rowsByKey) {
  const lc = lifecycle || {};
  const story = buildSlotStory(lc);
  const keys = slotRowKeys(lc);
  const rows = [];
  const heldKeys = [];
  const missingIds = [];
  for (const k of keys) {
    const r = lookupRow(rowsByKey, k);
    if (r) { rows.push(r); heldKeys.push(k); } else missingIds.push(k);
  }

  const har = buildHarFromRequests(rows, { title: `Slot ${story.adUnit || ''} ${story.key == null ? '' : story.key}`.trim() });

  const bodyStripped = [];
  har.log.entries.forEach((entry, i) => {
    const r = rows[i];
    if (r && r.captureSource === 'logcat') {
      entry.response = { ...entry.response, status: 0, statusText: '', content: { size: 0, mimeType: '' }, bodySize: 0 };
      entry.comment = 'request-only';
    } else if (r && r._bodyStripped) {
      bodyStripped.push(heldKeys[i]);
    }
  });

  const usb = isLogcatLifecycle(lc);
  const gamRow = lc.phases && lc.phases.gam && lc.phases.gam.row;
  har.log._inspectra = {
    ...(har.log._inspectra || {}),
    slot: {
      key: story.key == null ? null : story.key,
      verdict: story.verdict,
      status: story.status,
      steps: story.steps.map((s) => ({ id: s.id, state: s.state, rowKey: s.rowKey == null ? null : s.rowKey })),
      captureSource: usb ? 'logcat' : ((gamRow && gamRow.captureSource) || 'proxy'),
      requestOnly: usb,
      missingIds,
      bodyStripped,
    },
  };

  return { har, missingIds, count: har.log.entries.length, filename: slotHarFilename(lc) };
}
